import React, { useEffect } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Container,
  Typography,
  Button,
  CircularProgress,
  Box,
  CardMedia,
  CardContent,
  Grid,
  IconButton,
  Divider,
} from "@mui/material";
import { FETCH_CART } from "../graphql/queries";
import { Add, Remove } from "@mui/icons-material";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import { REMOVE_FROM_CART, UPDATE_CART_ITEMS } from "../graphql/mutations";
import PlaceholderPage from "./MyBiz/PlaceholderPage";
import { toast } from "react-toastify";
import CartSkeleton from "../Components/Skeletons/CartSkeleton";
import Cookies from "js-cookie";
import { removeLocalStorage } from "../utils/localStorage";
import { useAppBridge, safeNavigate } from "../Components/Shopify/ShopifyAppBridgeProvider";

const CartPage: React.FC = () => {
  const emptyImage1 = require("../assets/images/Empty1.png");
  const { isEmbedded } = useAppBridge();

  const [fetchCart, { data, loading, error }] = useLazyQuery(FETCH_CART);
  const [removeFromCart] = useMutation(REMOVE_FROM_CART);
  const [updateCartItems] = useMutation(UPDATE_CART_ITEMS);

  const [removeCartLoading, setRemoveCartLoading] = React.useState({
    selectedLineId: "",
    loading: false,
  });
  const [quantityLoading, setQuantityLoading] = React.useState(false);

  useEffect(() => {
    fetchCart(); // Backend fetches all user carts from database
  }, [fetchCart]);

  // REMOVE ITEM
  const handleRemoveItem = async (lineId: string, store: string, cartId: string) => {
    Cookies.set("shopDomain", store);
    setRemoveCartLoading({ selectedLineId: lineId, loading: true });
    await removeFromCart({
      variables: { cartId, lineIds: [lineId] },
      refetchQueries: [FETCH_CART],
    });
    setRemoveCartLoading({ selectedLineId: "", loading: false });
  };

  // QUANTITY DECREASE
  const handleDecrementQuantity = async (lineId: string, newQuantity: number, store: string, cartId: string) => {
    if (newQuantity === 0) {
      setQuantityLoading(true);
      handleRemoveItem(lineId, store, cartId).then(() => setQuantityLoading(false));
      return;
    }

    Cookies.set("shopDomain", store);
    setQuantityLoading(true);
    await updateCartItems({
      variables: { cartId, items: { id: lineId, quantity: newQuantity } },
      refetchQueries: [FETCH_CART],
    });
    setQuantityLoading(false);
  };

  // QUANTITY INCREASE
  const handleIncrementQuantity = async (lineId: string, newQuantity: number, store: string, cartId: string) => {
    Cookies.set("shopDomain", store);
    setQuantityLoading(true);
    await updateCartItems({
      variables: { cartId, items: { id: lineId, quantity: newQuantity } },
      refetchQueries: [FETCH_CART],
    });
    setQuantityLoading(false);
  };

  // STORE CHECKOUT
  const handleStoreCheckout = (store: string) => {
    const storeLines = data?.fetchCart.lines.filter(
      (line: any) => line.merchandise.shop_domain === store
    );

    if (!storeLines?.length) {
      toast.error("No items for this store.");
      return;
    }

    const checkoutUrl = storeLines[0].merchandise.checkoutUrl;
    if (!checkoutUrl) {
      toast.error("Checkout URL missing for this store.");
      return;
    }

    // Navigate to custom checkout page instead of Shopify's hosted checkout
    removeLocalStorage("shopifyCartId");

    // Use safe navigation helper that handles embedded context and fallbacks
    safeNavigate(checkoutUrl, isEmbedded);
  };

  const cart = data?.fetchCart;

  // Group lines by store WITHOUT changing layout
  const linesByStore = React.useMemo(() => {
    if (!cart?.lines) return {};
    return cart.lines.reduce((acc: any, line: any) => {
      const store = line.merchandise.shop_domain || "unknown-store";
      if (!acc[store]) acc[store] = [];
      acc[store].push(line);
      return acc;
    }, {});
  }, [cart]);


  if (error) {
    return (<div>Error loading cart: {error?.message}</div>
    );
  }

  if ((!cart || cart.lines.length === 0) && !loading) {
    return (
      <PlaceholderPage
        title="Cart"
        emptyStateText="Your cart is empty."
        emptyStateImage={emptyImage1}
        actionButtons={<></>}
      />
    );
  }

  return (
    <Container
      maxWidth="xl"
      id="CartContainer"
      sx={{ backgroundColor: "#f5f5f5", paddingTop: 2, paddingBottom: 2, borderRadius: 16 }}
      style={{ borderRadius: 16, justifyContent: "center", justifyItems: "center" }}
    >
      {quantityLoading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.3)",
            backdropFilter: "blur(2px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ color: "#e34cb5" }} />
        </Box>
      )}

      <Typography variant="h4" gutterBottom mt={2}>
        Your Cart
      </Typography>

      {loading && <CartSkeleton />}

      {/* ✅ SAME UI layout */}
      {!loading &&
        Object.entries(linesByStore).map(([store, storeLines]: any) => (
          <React.Fragment key={store}>
            {storeLines.map((line: any) => (
              <React.Fragment key={line.id}>
                <Grid
                  container
                  direction="row"
                  spacing={1}
                  maxWidth="md"
                  alignContent="center"
                  alignItems="center"
                >
                  <Grid item xs={2}>
                    {line.merchandise.image?.src ? (
                      <CardMedia
                        component="img"
                        sx={{ width: 150 }}
                        image={line.merchandise.image.src}
                        alt={line.merchandise.image.altText || "Product image"}
                      />
                    ) : (
                      <Box
                        role="img"
                        aria-label={`${line.merchandise.product.title} — no image available`}
                        sx={{
                          width: 150,
                          height: 150,
                          backgroundColor: "#ffffff",
                          border: "1px solid #eee",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#bdbdbd",
                        }}
                      >
                        <ImageNotSupportedOutlinedIcon sx={{ fontSize: 48 }} />
                        <Typography variant="caption" sx={{ mt: 0.5 }}>
                          No image
                        </Typography>
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={4}>
                    <CardContent>
                      <Typography variant="subtitle1">
                        {line.merchandise.product.title}
                      </Typography>

                      {line.merchandise.selectedOptions
                        .filter((option: any) => !(option.name === "Title" && option.value === "Default Title"))
                        .map((option: any) => (
                          <Typography key={option.name} variant="overline">
                            {option.name}: {option.value}
                          </Typography>
                        )
                      )}

                      <Button
                        variant="outlined"
                        color="error"
                        sx={{ mt: 1 }}
                        size="small"
                        onClick={() => handleRemoveItem(line.id, line.merchandise.shop_domain, line.cart_id)}
                        disabled={removeCartLoading.loading}
                      >
                        {(removeCartLoading.loading && removeCartLoading.selectedLineId === line.id) && (
                          <CircularProgress size={18} sx={{ mr: 2 }} />
                        )}
                        Remove
                      </Button>
                    </CardContent>
                  </Grid>

                  <Grid item xs={3}>
                    <Box display="flex" alignItems="center">
                      <IconButton
                        color="secondary"
                        onClick={() => handleDecrementQuantity(line.id, line.quantity - 1, line.merchandise.shop_domain, line.cart_id)}
                      >
                        <Remove />
                      </IconButton>

                      <Typography variant="h6" px={1}>
                        {line.quantity}
                      </Typography>

                      <IconButton
                        color="secondary"
                        onClick={() => handleIncrementQuantity(line.id, line.quantity + 1, line.merchandise.shop_domain, line.cart_id)}
                      >
                        <Add />
                      </IconButton>
                    </Box>
                  </Grid>

                  <Grid item xs={3}>
                    <Typography variant="body1">
                      ${(line.merchandise.price * line.quantity)?.toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ marginTop: "1px", borderWidth: "1px" }} />
              </React.Fragment>
            ))}

            {/* ✅ Store-wise checkout — clean + small + matches old design */}
            <Box textAlign="right" mt={2} mb={3}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleStoreCheckout(store)}
              >
                Proceed to Checkout – {store}
              </Button>
            </Box>
          </React.Fragment>
        ))}
    </Container>
  );
};

export default CartPage;